'use client';

import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { ResponsiveDrawer as Drawer } from '@/components/ui/responsive-drawer';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

export interface PickerOption<T extends string | null> {
  value: T;
  label: string;
  /** Optional leading icon component (Phosphor icon). */
  Icon?: React.ComponentType<{
    className?: string;
    size?: number;
    weight?: 'bold' | 'regular' | 'duotone' | 'fill';
  }>;
}

interface PickerDrawerProps<T extends string | null> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  options: PickerOption<T>[];
  value: T;
  onSelect: (value: T) => void;
  onClear?: () => void;
}

export function PickerDrawer<T extends string | null>({
  open,
  onOpenChange,
  title,
  options,
  value,
  onSelect,
  onClear,
}: PickerDrawerProps<T>) {
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        data-tokens="v2"
        className="bg-surface px-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="flex flex-row items-center justify-between px-0">
          <DrawerTitle className="text-lg font-semibold text-text-primary">{title}</DrawerTitle>
          <DrawerDescription className="sr-only">
            Escolha uma opção para {title}.
          </DrawerDescription>
          {onClear && (
            <button
              type="button"
              className="text-sm text-text-secondary hover:text-text-primary"
              onClick={() => {
                onClear();
                onOpenChange(false);
              }}
            >
              Limpar
            </button>
          )}
        </DrawerHeader>

        <RadioGroup
          value={String(value ?? '')}
          onValueChange={(v) => {
            const next = (v === '' ? null : v) as T;
            onSelect(next);
            onOpenChange(false);
          }}
          className="gap-0"
        >
          {options.map((opt) => {
            const id = `picker-${opt.value ?? 'null'}`;
            const selected = String(value ?? '') === String(opt.value ?? '');
            return (
              <Label
                key={id}
                htmlFor={id}
                className={cn(
                  'flex h-14 cursor-pointer items-center justify-between gap-3 border-b border-divider px-2 last:border-b-0',
                  selected && 'text-foreground',
                )}
              >
                <span className="flex min-w-0 items-center gap-3">
                  {opt.Icon && (
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-surface-sunken">
                      <opt.Icon className="size-4 text-text-primary" weight="duotone" />
                    </span>
                  )}
                  <span className="truncate text-sm font-medium">{opt.label}</span>
                </span>
                <RadioGroupItem id={id} value={String(opt.value ?? '')} />
              </Label>
            );
          })}
        </RadioGroup>
      </DrawerContent>
    </Drawer>
  );
}
